import React from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Edit, Trash2, CheckCircle2, Clock, Repeat } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const AssignmentTable = ({ assignments, employees, schedules, cyclePatterns, handleOpenEditModal, handleOpenDeleteDialog }) => {
  const { t, i18n } = useTranslation();
  const formatDate = (dateStr) => {
    if (!dateStr) return t("planning.assignmentsPanel.noEndDate");
    return new Intl.DateTimeFormat(i18n.language || "fr", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(dateStr));
  };
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.last_name} ${emp.first_name}` : t("planning.assignmentsPanel.unknownEmployee");
  };
  const getScheduleName = (id) => schedules.find((s) => s.id === id)?.name || t("planning.assignmentsPanel.unknownSchedule");
  const getCycleName = (id) => cyclePatterns.find((c) => c.id === id)?.name || t("planning.assignmentsPanel.unknownCycle");

  if (assignments.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>{t("planning.assignmentsPanel.emptyTitle")}</p>
          <p className="text-sm mt-1">{t("planning.assignmentsPanel.emptyDescription")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("planning.assignmentsPanel.table.employee")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.type")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.scheduleCycle")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.startDate")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.endDate")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.status")}</TableHead>
                <TableHead>{t("planning.assignmentsPanel.table.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell className="font-medium">{getEmployeeName(assignment.employee_id)}</TableCell>
                  <TableCell>
                    {assignment.cycle_pattern_id ? (
                      <Badge variant="outline" className="gap-1"><Repeat className="h-3 w-3" />{t("planning.assignmentsPanel.rotatingCycle")}</Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1"><Calendar className="h-3 w-3" />{t("planning.scheduleManager.scheduleTypes.FIXED")}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {assignment.cycle_pattern_id ? (
                      <div>
                        <p className="font-medium">{getCycleName(assignment.cycle_pattern_id)}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("planning.assignmentsPanel.startsAtWeek", { week: assignment.cycle_start_week })}
                        </p>
                      </div>
                    ) : (
                      getScheduleName(assignment.schedule_id)
                    )}
                  </TableCell>
                  <TableCell>{formatDate(assignment.start_date)}</TableCell>
                  <TableCell>{formatDate(assignment.end_date)}</TableCell>
                  <TableCell>
                    {assignment.status === "approved" ? (
                      <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />{t("planning.assignmentsPanel.statuses.approved")}</Badge>
                    ) : assignment.status === "pending" ? (
                      <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600"><Clock className="h-3 w-3" />{t("planning.assignmentsPanel.statuses.pending")}</Badge>
                    ) : assignment.status === "rejected" ? (
                      <Badge variant="outline" className="gap-1 text-red-600 border-red-600"><Clock className="h-3 w-3" />{t("planning.assignmentsPanel.statuses.rejected")}</Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-muted-foreground"><Clock className="h-3 w-3" />{assignment.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(assignment)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDeleteDialog(assignment)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AssignmentTable;
